import React, { useState, useEffect } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { List, Card, Space, Typography, Table, Tag, Row, Col, Statistic, Tooltip, Button, Popconfirm, Alert } from "@pankod/refine-antd";
import { HistoryOutlined, CheckCircleOutlined, SyncOutlined, DatabaseOutlined, ClockCircleOutlined, PlayCircleOutlined, StopOutlined, RetweetOutlined } from "@ant-design/icons";
import { getBridgeTaskHistory, getBridgeTaskStats, abortBridgeTask, retryBridgeTask, runBridgeSync } from "Services/productServices";
import { format } from "date-fns";
import toast from "react-hot-toast";

const { Text, Title } = Typography;

export const BridgeTasks: React.FC<IResourceComponentsProps> = () => {
  const [history, setHistory] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        getBridgeTaskHistory(50),
        getBridgeTaskStats()
      ]);
      setHistory(historyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load bridge tasks data:", error);
      toast.error("Failed to load task statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const handleAbort = async (jobId: string, type: any) => {
    setActionLoading(jobId);
    try {
      await abortBridgeTask(jobId, type === 'cms' ? 'cms' : 'pronto');
      toast.success("Task abort signal sent");
      await loadData();
    } catch (error) {
      toast.error("Failed to abort task");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetry = async (jobId: string, type: any) => {
    setActionLoading(jobId);
    try {
      await retryBridgeTask(jobId, type === 'cms' ? 'cms' : 'pronto');
      toast.success("Task scheduled for retry");
      await loadData();
    } catch (error) {
      toast.error("Failed to retry task");
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartSync = async () => {
    setLoading(true);
    try {
      await runBridgeSync("full");
      toast.success("Full CMS Sync started");
      await loadData();
    } catch (error) {
      toast.error("Failed to start sync");
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'success') return <Tag color="success">Completed</Tag>;
    if (s === 'active' || s === 'running') return <Tag color="processing" icon={<SyncOutlined spin />}>Running</Tag>;
    if (s === 'waiting') return <Tag color="warning">Waiting</Tag>;
    if (s === 'failed') return <Tag color="error">Failed</Tag>;
    return <Tag>{status}</Tag>;
  };

  const getTaskTypeTag = (type: string) => {
    if (type === 'cms') return <Tag color="blue">CMS Sync</Tag>;
    if (type === 'pronto') return <Tag color="purple">Pronto Sync</Tag>;
    return <Tag>{type.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (text: string) => format(new Date(text), "MMM d, HH:mm:ss"),
      width: 160
    },
    {
      title: "Task Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => (
        <Space>
           {getTaskTypeTag(record.type)}
           <Text strong>{name}</Text>
        </Space>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => getStatusTag(status)
    },
    {
      title: "Products",
      dataIndex: "processedCount",
      key: "processedCount",
      render: (count: number) => count || "-"
    },
    {
      title: "Drafts",
      dataIndex: "createdCount",
      key: "createdCount",
      render: (count: number) => count || "-"
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (ms: number) => ms ? `${(ms / 1000).toFixed(1)}s` : "-"
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => {
        const canAbort = record.status === 'active' || record.status === 'waiting' || record.status === 'running';
        const canRetry = record.status === 'failed' && record.id; // Only jobs still in Redis can be retried
        
        return (
          <Space>
            {canAbort && (
              <Popconfirm title="Abort this task?" onConfirm={() => handleAbort(record.id, record.type)}>
                <Button 
                  size="small" 
                  danger 
                  icon={<StopOutlined />} 
                  loading={actionLoading === record.id}
                >
                  Abort
                </Button>
              </Popconfirm>
            )}
            {canRetry && (
              <Button 
                size="small" 
                type="primary" 
                icon={<RetweetOutlined />} 
                loading={actionLoading === record.id}
                onClick={() => handleRetry(record.id, record.type)}
              >
                Retry
              </Button>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <List 
        title="Bridge Tasks & Synchronization Statistics"
        headerProps={{
          extra: (
            <Space>
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />} 
                onClick={handleStartSync}
                loading={loading}
              >
                Run CMS Sync Now
              </Button>
              <Button 
                icon={<SyncOutlined />} 
                onClick={loadData}
                loading={loading}
              >
                Refresh
              </Button>
            </Space>
          )
        }}
      >
        <Alert
          message="Enhanced Sync Features Active"
          description="The sync process now automatically discovers new products in Pronto and onboards them to Strapi as Drafts with mapped metadata (Varietals, Regions, Styles, etc.)."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic 
                title="Total Syncs" 
                value={stats?.totalSyncs || 0} 
                prefix={<HistoryOutlined />} 
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic 
                title="Success Rate" 
                value={stats?.successRate || 0} 
                precision={1}
                suffix="%" 
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} 
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card>
              <Statistic 
                title="Products (24h)" 
                value={stats?.productsSynced24h || 0} 
                prefix={<DatabaseOutlined style={{ color: '#1890ff' }} />} 
              />
            </Card>
          </Col>
          <Col span={5}>
            <Card>
              <Statistic 
                title="New Drafts (24h)" 
                value={stats?.productsCreated24h || 0} 
                prefix={<CheckCircleOutlined style={{ color: '#eb2f96' }} />} 
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic 
                title="Last Sync" 
                value={stats?.lastSync ? format(new Date(stats.lastSync), "HH:mm") : "N/A"} 
                prefix={<ClockCircleOutlined />} 
              />
            </Card>
          </Col>
        </Row>

        <Card title="Task History (Last 50 Entries)">
          <Table 
            dataSource={history} 
            columns={columns} 
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 15 }}
          />
        </Card>
      </List>
    </div>
  );
};
